import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import {
  adminNotificationsApi,
  AdminNotification,
  AdminResetLinkResponse,
  MobileResetCodeResponse,
} from '../../services/adminNotificationsApi';
import { resetMobileUserPassword } from '../../services/mobileUsersApi';
import { toUserMessage } from '../../lib/http';
import { formatDate } from '../../utils/dates';
import { logger } from '../../lib/logger';

const notifLabel = (type: string): string => {
  if (type === 'password_reset_mobile') return 'Reset MDP (mobile)';
  if (type === 'password_reset_request') return 'Demande reset MDP (app)';
  return 'Reset MDP (admin email)';
};

const notifSubtitle = (n: AdminNotification): string => {
  if (n.type === 'password_reset_admin') return n.email || '';
  if (n.name) return `${n.name} — ${n.phoneNumber || ''}`;
  return n.phoneNumber || '';
};

export const AdminNotificationsPanel: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selected, setSelected] = useState<AdminNotification | null>(null);

  // Réinitialisation de mot de passe (type password_reset_request)
  const [resetPwd, setResetPwd] = useState('');
  const [resetResult, setResetResult] = useState<string | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  // Reset d'un compte admin : lien FRAIS généré à la demande (action auditée
  // côté serveur), gardé en mémoire le temps du dialogue, jamais stocké.
  const [adminLink, setAdminLink] = useState<AdminResetLinkResponse | null>(null);
  const [adminLinkLoading, setAdminLinkLoading] = useState(false);
  const [adminLinkError, setAdminLinkError] = useState<string | null>(null);

  // Reset mobile (SMS non reçu) : code FRAIS émis à la demande (audité côté
  // serveur, invalide le précédent), gardé en mémoire le temps du dialogue.
  const [mobileCode, setMobileCode] = useState<MobileResetCodeResponse | null>(null);
  const [mobileCodeLoading, setMobileCodeLoading] = useState(false);
  const [mobileCodeError, setMobileCodeError] = useState<string | null>(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      setUnreadCount(await adminNotificationsApi.getUnreadCount());
    } catch { /* silencieux */ }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30_000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    try {
      const res = await adminNotificationsApi.getNotifications(false, 30);
      setNotifications(res.notifications);
      setUnreadCount(res.unreadCount);
    } catch { /* silencieux */ }
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (notif: AdminNotification) => {
    setSelected(notif);
    setResetPwd('');
    setResetResult(null);
    setResetError(null);
    setAdminLink(null);
    setAdminLinkError(null);
    setMobileCode(null);
    setMobileCodeError(null);
    if (!notif.read) handleMarkAsRead(notif.id);
  };

  const handleGenerateMobileCode = async () => {
    if (!selected) return;
    setMobileCodeLoading(true);
    setMobileCodeError(null);
    try {
      setMobileCode(await adminNotificationsApi.generateMobileResetCode(selected.id));
    } catch (e: unknown) {
      logger.error('Erreur génération code reset mobile', e);
      setMobileCodeError(toUserMessage(e, 'Impossible de générer le code. Veuillez réessayer.'));
    } finally {
      setMobileCodeLoading(false);
    }
  };

  const handleGenerateAdminLink = async () => {
    if (!selected) return;
    setAdminLinkLoading(true);
    setAdminLinkError(null);
    try {
      setAdminLink(await adminNotificationsApi.generateAdminResetLink(selected.id));
    } catch (e: unknown) {
      logger.error('Erreur génération lien reset admin', e);
      setAdminLinkError(toUserMessage(e, 'Impossible de générer le lien. Veuillez réessayer.'));
    } finally {
      setAdminLinkLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setSelected(null);
    setAdminLink(null);
    setMobileCode(null);
  };

  const handleResetPassword = async () => {
    const userId = selected?.userId;
    if (!userId) return;
    setResetLoading(true);
    setResetError(null);
    try {
      const res = await resetMobileUserPassword(userId, resetPwd.trim() || undefined);
      setResetResult(res.password || resetPwd.trim());
    } catch (e: unknown) {
      logger.error('Erreur reset mot de passe', e);
      setResetError(toUserMessage(e, 'Échec de la réinitialisation. Veuillez réessayer.'));
    } finally {
      setResetLoading(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    await adminNotificationsApi.markAsRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleMarkAllRead = async () => {
    await adminNotificationsApi.markAllRead();
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  return (
    <>
      {/* Cloche */}
      <IconButton color="inherit" onClick={handleOpen} size="large">
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      {/* Liste des notifications */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380, maxHeight: 500 } }}
      >
        {/* En-tête */}
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Notifications
            {unreadCount > 0 && (
              <Chip label={unreadCount} color="error" size="small" sx={{ ml: 0.5, height: 18, fontSize: 11 }} />
            )}
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllRead}>Tout marquer lu</Button>
          )}
        </Box>

        {/* Items */}
        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Aucune notification</Typography>
          </Box>
        ) : (
          notifications.map((notif) => (
            <MenuItem
              key={notif.id}
              onClick={() => handleSelect(notif)}
              sx={{
                bgcolor: notif.read ? 'transparent' : 'action.hover',
                flexDirection: 'column',
                alignItems: 'flex-start',
                whiteSpace: 'normal',
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={0.5} width="100%">
                {notif.type === 'password_reset_admin'
                  ? <EmailIcon fontSize="small" color="warning" />
                  : <PhoneIcon fontSize="small" color="primary" />}
                <Typography variant="body2" fontWeight={notif.read ? 400 : 700} sx={{ flex: 1 }}>
                  {notifLabel(notif.type)}
                </Typography>
                {!notif.read && (
                  <Chip label="Nouveau" color="error" size="small" sx={{ height: 16, fontSize: 10 }} />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {notifSubtitle(notif)}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25 }}>
                {formatDate(notif.createdAt, 'dateTimeSeconds')}
              </Typography>
            </MenuItem>
          ))
        )}
      </Menu>

      {/* Dialog détail */}
      <Dialog open={Boolean(selected)} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selected?.type === 'password_reset_request'
            ? 'Demande de réinitialisation — Utilisateur app'
            : selected?.type === 'password_reset_mobile'
            ? 'Réinitialisation mot de passe — Mobile'
            : 'Réinitialisation mot de passe — Admin'}
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box display="flex" flexDirection="column" gap={2}>
              {selected.type === 'password_reset_request' ? (
                <>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Utilisateur</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {selected.name || '—'}
                      {selected.userType ? ` (${selected.userType})` : ''}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Téléphone</Typography>
                    <Typography variant="body1" fontWeight={600}>{selected.phoneNumber || '—'}</Typography>
                  </Box>
                  <Divider />
                  {resetResult ? (
                    <Stack spacing={1}>
                      <Alert severity="success">Mot de passe réinitialisé.</Alert>
                      <Typography variant="caption" color="text.secondary">
                        Nouveau mot de passe (à communiquer à l'utilisateur) :
                      </Typography>
                      <Chip
                        label={resetResult}
                        color="primary"
                        sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 18, height: 36, px: 1 }}
                        onClick={() => navigator.clipboard.writeText(resetResult)}
                      />
                      <Typography variant="caption" color="text.disabled">Cliquez pour copier.</Typography>
                    </Stack>
                  ) : (
                    <Stack spacing={1.5}>
                      <TextField
                        label="Nouveau mot de passe"
                        placeholder="Laisser vide pour générer automatiquement"
                        value={resetPwd}
                        onChange={(e) => setResetPwd(e.target.value)}
                        size="small"
                        fullWidth
                        autoComplete="new-password"
                      />
                      {resetError && <Alert severity="error">{resetError}</Alert>}
                      <Button
                        variant="contained"
                        onClick={handleResetPassword}
                        disabled={resetLoading || !selected.userId}
                        startIcon={resetLoading ? <CircularProgress size={16} /> : undefined}
                      >
                        {resetPwd.trim() ? 'Appliquer le mot de passe' : 'Générer & appliquer'}
                      </Button>
                    </Stack>
                  )}
                </>
              ) : selected.type === 'password_reset_mobile' ? (
                <>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Téléphone</Typography>
                    <Typography variant="body1" fontWeight={600}>{selected.phoneNumber}</Typography>
                  </Box>
                  {mobileCode ? (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Code à communiquer (valable {Math.round(mobileCode.expiresIn / 60)} min, non conservé)
                      </Typography>
                      <Box mt={0.5}>
                        <Chip
                          label={mobileCode.otpCode}
                          color="primary"
                          sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 20, height: 36, px: 1 }}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        Aucun code n'est conservé. Si l'utilisateur n'a pas reçu le SMS, générez un
                        nouveau code (il remplace le précédent) ; l'action est enregistrée dans le
                        journal d'audit.
                      </Typography>
                      {mobileCodeError && <Alert severity="error">{mobileCodeError}</Alert>}
                      <Button
                        variant="contained"
                        onClick={handleGenerateMobileCode}
                        disabled={mobileCodeLoading}
                        startIcon={mobileCodeLoading ? <CircularProgress size={16} /> : undefined}
                      >
                        Générer un code
                      </Button>
                    </Stack>
                  )}
                </>
              ) : (
                <>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body1" fontWeight={600}>{selected.email}</Typography>
                  </Box>
                  {adminLink?.resetLink ? (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Lien de réinitialisation (usage unique, non conservé)
                      </Typography>
                      <Box mt={0.5} p={1.5} sx={{ bgcolor: 'grey.100', borderRadius: 1, wordBreak: 'break-all' }}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {adminLink.resetLink}
                        </Typography>
                      </Box>
                      <Box mt={1} display="flex" gap={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigator.clipboard.writeText(adminLink.resetLink!)}
                        >
                          Copier le lien
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          href={adminLink.resetLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Ouvrir le lien
                        </Button>
                      </Box>
                    </Box>
                  ) : adminLink ? (
                    <Alert severity={adminLink.emailEnvoye ? 'success' : 'warning'}>
                      {adminLink.emailEnvoye
                        ? "Lien indisponible : Firebase a envoyé l'email de réinitialisation."
                        : "Lien indisponible et l'email n'a pas pu être envoyé. Réessayez plus tard."}
                    </Alert>
                  ) : (
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        Aucun lien n'est conservé. Générez-en un nouveau pour le transmettre à
                        l'administrateur ; l'action est enregistrée dans le journal d'audit.
                      </Typography>
                      {adminLinkError && <Alert severity="error">{adminLinkError}</Alert>}
                      <Button
                        variant="contained"
                        onClick={handleGenerateAdminLink}
                        disabled={adminLinkLoading}
                        startIcon={adminLinkLoading ? <CircularProgress size={16} /> : undefined}
                      >
                        Générer un lien de réinitialisation
                      </Button>
                    </Stack>
                  )}
                </>
              )}
              <Divider />
              <Typography variant="caption" color="text.disabled">
                {formatDate(selected.createdAt, 'dateTimeSeconds')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
